import {
  queryKeys,
  transformBenefitsForSave,
} from "@platform/btx-plan-designer";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import type {
  LocCoverageType,
  RfpBenefitPlan,
  RfpData,
} from "@platform/btx-plan-designer";
import type { AxiosInstance } from "axios";

// The Incumbent/Requested column mirrors the RFP's own requested plans, which
// are benefit plans, not proposal plans — their edits go to the same endpoint
// the Build tab autosaves through, not to /proposal-plans/:id/benefits.
const patchBenefitPlanBenefits = async (
  axiosInstance: AxiosInstance,
  planId: string,
  planType: RfpBenefitPlan["type"],
  benefits: RfpBenefitPlan["benefits"],
): Promise<void> => {
  await axiosInstance.patch(`btx/v2/benefit-plans/${planId}`, {
    type: planType,
    benefits,
  });
};

// Replaces one benefit plan's benefits wherever it appears in the loaded RFP,
// leaving every other plan untouched so a save can't stomp a concurrent edit.
const patchPlanBenefitsInRfpCache = (
  rfp: RfpData | undefined,
  planId: string,
  benefits: RfpBenefitPlan["benefits"],
): RfpData | undefined => {
  if (!rfp) return rfp;
  return {
    ...rfp,
    coverages: rfp.coverages.map((coverage) => ({
      ...coverage,
      benefitPlans: coverage.benefitPlans.map((plan) =>
        plan.id === planId ? { ...plan, benefits } : plan,
      ),
    })),
  };
};

const findPlanBenefitsInRfpCache = (
  rfp: RfpData | undefined,
  planId: string,
): RfpBenefitPlan["benefits"] | undefined =>
  rfp?.coverages
    .flatMap((coverage) => coverage.benefitPlans)
    .find((plan) => plan.id === planId)?.benefits;

interface UseRequestedPlanBenefitsMutationOptions {
  /** BTX (eco) RFP id — the key the loaded RFP is cached under. */
  ecoRfpId: string;
  axiosInstance: AxiosInstance;
  coverageType: LocCoverageType | undefined;
  onSaveError?: (err: Error) => void;
}

interface RequestedPlanMutationVariables {
  planId: string;
  planType: RfpBenefitPlan["type"];
  /** API-shaped body for the PATCH. */
  apiBenefits: RfpBenefitPlan["benefits"];
  /**
   * The same benefits in grid shape, for the optimistic cache write. The loaded
   * RFP stores benefits already run through `transformFromApi`, so caching the
   * API body instead would nest the flat keys the grid reads and blank every
   * cell in the column until the next refetch.
   */
  gridBenefits: RfpBenefitPlan["benefits"];
}

interface RequestedPlanMutationContext {
  originalBenefits: RfpBenefitPlan["benefits"] | undefined;
}

export const useRequestedPlanBenefitsMutation = ({
  ecoRfpId,
  axiosInstance,
  coverageType,
  onSaveError,
}: UseRequestedPlanBenefitsMutationOptions): {
  saveRequestedPlanBenefits: (plan: RfpBenefitPlan) => void;
} => {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.rfp(ecoRfpId);

  const { mutate } = useMutation({
    mutationFn: ({
      planId,
      planType,
      apiBenefits,
    }: RequestedPlanMutationVariables) =>
      patchBenefitPlanBenefits(axiosInstance, planId, planType, apiBenefits),
    onMutate: async ({
      planId,
      gridBenefits,
    }: RequestedPlanMutationVariables): Promise<RequestedPlanMutationContext> => {
      await queryClient.cancelQueries({ queryKey });
      const originalBenefits = findPlanBenefitsInRfpCache(
        queryClient.getQueryData<RfpData>(queryKey),
        planId,
      );
      queryClient.setQueryData<RfpData>(queryKey, (old) =>
        patchPlanBenefitsInRfpCache(old, planId, gridBenefits),
      );
      return { originalBenefits };
    },
    onSuccess: () => {
      // Mark stale without refetching: an immediate refetch rebuilds the whole
      // SBS grid after every cell edit and can race a sibling plan's still
      // in-flight save. The cache already shows the edited value.
      queryClient.invalidateQueries({ queryKey, refetchType: "none" });
    },
    onError: (
      err: Error,
      { planId }: RequestedPlanMutationVariables,
      context: RequestedPlanMutationContext | undefined,
    ) => {
      if (context) {
        queryClient.setQueryData<RfpData>(queryKey, (old) =>
          patchPlanBenefitsInRfpCache(old, planId, context.originalBenefits),
        );
      }
      onSaveError?.(err);
    },
  });

  const saveRequestedPlanBenefits = useCallback(
    (plan: RfpBenefitPlan) => {
      if (!plan.id || !coverageType) return;
      // The grid holds the plan's full benefit body, so this PATCH carries the
      // v5 fields back unchanged alongside the edited extended ones — the
      // benefits update is a full replace, the same as the Build tab's.
      mutate({
        planId: plan.id,
        planType: plan.type,
        apiBenefits: transformBenefitsForSave(
          plan.benefits,
          coverageType,
        ) as RfpBenefitPlan["benefits"],
        gridBenefits: plan.benefits,
      });
    },
    [mutate, coverageType],
  );

  return { saveRequestedPlanBenefits };
};
